/**
 * StageRatingWidget — per-stage thumbs up/down rating control.
 *
 * Renders two right-aligned MUI IconButtons (👍 / 👎). The active vote is shown
 * with a *filled* icon (`ThumbUpAlt`/`ThumbDownAlt`) and the inactive state with
 * an *outlined* icon (`ThumbUpOffAlt`/`ThumbDownOffAlt`). Each button carries
 * `aria-pressed` reflecting whether it is the current vote.
 *
 * Behavior (spec §3.2):
 *   - 👍 click: new value = (current === 'up') ? 'cleared' : 'up'. Optimistically
 *     update local state, POST via `submitStageRating`, then `onChange(value)`.
 *   - 👎 click: if not already 'down', set 'down', POST, `onChange('down')`, then
 *     `onThumbsDown()` to open the feedback dialog. If already 'down' (toggle
 *     off), set 'cleared', POST cleared, `onChange(null)`, and do NOT reopen the
 *     feedback dialog.
 *   - Writes are best-effort: a rejected POST reverts the optimistic state and is
 *     swallowed (console.warn only) — it never throws to the caller.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ThumbUpAlt from '@mui/icons-material/ThumbUpAlt';
import ThumbUpOffAlt from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownAlt from '@mui/icons-material/ThumbDownAlt';
import ThumbDownOffAlt from '@mui/icons-material/ThumbDownOffAlt';
import {
  RatingValue,
  backendStageNumber,
  submitStageRating,
} from '../api/stageRatingFunctions';
import { WorkflowType } from './WorkflowTypeGrid';

export interface StageRatingWidgetProps {
  workflowType: WorkflowType;
  /** 0-based current stage index. */
  uiStageIndex: number;
  /** Human label, e.g. "Clause Logic › Final Review". */
  stageLabel: string;
  flowId?: string | null;
  /** The user's saved vote for this stage, from the parent. */
  currentValue?: RatingValue | null;
  /** Open the feedback dialog (👎). */
  onThumbsDown: () => void;
  /** Notify the parent of a new vote so it can keep its map fresh. */
  onChange?: (value: RatingValue | null) => void;
}

export const StageRatingWidget: React.FC<StageRatingWidgetProps> = ({
  workflowType,
  uiStageIndex,
  stageLabel,
  flowId,
  currentValue = null,
  onThumbsDown,
  onChange,
}) => {
  const [value, setValue] = useState<RatingValue | null>(currentValue ?? null);

  // Keep local state in sync if the parent's saved vote changes.
  useEffect(() => {
    setValue(currentValue ?? null);
  }, [currentValue]);

  const stage = backendStageNumber(workflowType, uiStageIndex);

  /**
   * Best-effort persist. Optimistically applies `nextValue` locally and notifies
   * the parent; on a rejected POST it reverts to `prevValue`. Never throws.
   */
  const persist = useCallback(
    async (
      nextValue: RatingValue | null,
      submitValue: 'up' | 'down' | 'cleared'
    ) => {
      const prevValue = value;
      setValue(nextValue);
      onChange?.(nextValue);
      try {
        await submitStageRating({
          workflowType,
          stage,
          stageLabel,
          value: submitValue,
          flowId,
        });
      } catch (err) {
        // Best-effort: revert optimistic state, never block the workflow.
        setValue(prevValue);
        onChange?.(prevValue);
        // eslint-disable-next-line no-console
        console.warn('Stage rating submit failed; reverting.', err);
      }
    },
    [value, onChange, workflowType, stage, stageLabel, flowId]
  );

  const handleThumbsUp = useCallback(() => {
    const nextValue: RatingValue | null = value === 'up' ? null : 'up';
    const submitValue = value === 'up' ? 'cleared' : 'up';
    void persist(nextValue, submitValue);
  }, [value, persist]);

  const handleThumbsDown = useCallback(() => {
    if (value === 'down') {
      // Toggle off — clear, do NOT reopen feedback.
      void persist(null, 'cleared');
      return;
    }
    void persist('down', 'down');
    onThumbsDown();
  }, [value, persist, onThumbsDown]);

  const isUp = value === 'up';
  const isDown = value === 'down';

  return (
    <Box
      data-tour-id="stage-rating"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      <Tooltip title="Helpful" arrow placement="top">
        <IconButton
          size="small"
          aria-label="Helpful"
          aria-pressed={isUp}
          color={isUp ? 'primary' : 'default'}
          onClick={handleThumbsUp}
        >
          {isUp ? (
            <ThumbUpAlt fontSize="small" />
          ) : (
            <ThumbUpOffAlt fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      <Tooltip title="Not helpful" arrow placement="top">
        <IconButton
          size="small"
          aria-label="Not helpful"
          aria-pressed={isDown}
          color={isDown ? 'error' : 'default'}
          onClick={handleThumbsDown}
        >
          {isDown ? (
            <ThumbDownAlt fontSize="small" />
          ) : (
            <ThumbDownOffAlt fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default StageRatingWidget;
